import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from './components/Header';
import About from './components/TechnicalBackground';
import Founder from './components/Founder';
import Projects from './components/Projects';
import Certificates from './components/Certificates';
import Contact from './components/Contact';
import './App.css';  // Make sure this line is present

const pageVariants = {
  initial: {
    opacity: 0,
    y: 40,
    scale: 0.98,
  },
  in: {
    opacity: 1,
    y: 0,
    scale: 1,
  },
  out: {
    opacity: 0,
    y: -40,
    scale: 1.02,
  },
};

const pageTransition = {
  type: 'spring',
  stiffness: 100,
  damping: 20,
  duration: 0.8,
};

// Staggered animation for main content sections
const staggerContainer = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.3,
    },
  },
};

const staggerItem = {
  initial: { opacity: 0, y: 30 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 15,
    },
  },
};

function App() {
  return (
    <AnimatePresence mode="wait">
      <motion.div
        className="App"
        initial="initial"
        animate="in"
        exit="out"
        variants={pageVariants}
        transition={pageTransition}
      >
        {/* Animated background orbs */}
        <motion.div
          style={{
            position: 'fixed',
            width: '500px',
            height: '500px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(124, 58, 237, 0.15) 0%, transparent 70%)',
            filter: 'blur(60px)',
            top: '10%',
            left: '-10%',
            pointerEvents: 'none',
            zIndex: 0,
          }}
          animate={{
            x: [0, 50, 0],
            y: [0, 30, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          style={{
            position: 'fixed',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(0, 212, 255, 0.12) 0%, transparent 70%)',
            filter: 'blur(50px)',
            bottom: '20%',
            right: '-5%',
            pointerEvents: 'none',
            zIndex: 0,
          }}
          animate={{
            x: [0, -40, 0],
            y: [0, -50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          style={{
            position: 'fixed',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(244, 114, 182, 0.1) 0%, transparent 70%)',
            filter: 'blur(40px)',
            top: '50%',
            left: '30%',
            pointerEvents: 'none',
            zIndex: 0,
          }}
          animate={{
            x: [0, 30, -20, 0],
            y: [0, -20, 30, 0],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />

        <Header />
        
        <motion.main
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          style={{ position: 'relative', zIndex: 1 }}
        >
          <motion.div variants={staggerItem}>
            <Founder />
          </motion.div>
          <motion.div variants={staggerItem}>
            <Projects />
          </motion.div>
          <motion.div variants={staggerItem}>
            <Certificates />
          </motion.div>
          <motion.div variants={staggerItem}>
            <About />
          </motion.div>
          <motion.div variants={staggerItem}>
            <Contact />
          </motion.div>
        </motion.main>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.6 }}
          whileHover={{ scale: 1.02 }}
          style={{
            textAlign: 'center',
            padding: '2rem 0',
            marginTop: '4rem',
          }}
        >
          <span style={{ opacity: 0.6 }}>Built with</span>{' '}
          <motion.span
            animate={{ 
              scale: [1, 1.2, 1],
            }}
            transition={{ 
              duration: 1.5, 
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            style={{ display: 'inline-block' }}
          >
            💜
          </motion.span>{' '}
          <span style={{ opacity: 0.6 }}>by</span>{' '}
          <span style={{ 
            background: 'linear-gradient(135deg, var(--aurora-1), var(--aurora-2))',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 600,
          }}>
            Halil Ibrahim Demir
          </span>{' '}
          <span style={{ opacity: 0.5 }}>© {new Date().getFullYear()}</span>
        </motion.p>
      </motion.div>
    </AnimatePresence>
  );
}

export default App;
